
import React, { useState, useMemo, useEffect, type Dispatch, type SetStateAction } from 'react';
import type { Customer, FilterType, ProductData } from '../../type';
import { formatCaratWeight, formatDiamondQuality } from '../../lib/productConfigFormat';
import { METAL_OPTIONS } from './constants';
import { ConfigRow } from './ConfigRow';
import { OptionButton } from './OptionButton';
import { MetalButton } from './MetalButton';
import { QuantitySelector } from './QuantitySelector';
import { Button } from '../../components/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@univdiam/ui/components/dialog';
import { Input } from '@univdiam/ui/components/input';
import { Textarea } from '@univdiam/ui/components/textarea';
import { Label } from '@univdiam/ui/components/label';
import { toast } from 'sonner';
import { Loader2, Sparkles, SlidersHorizontal } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@univdiam/ui/components/select';

const SparkleIcon = () => (
    <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center">
        <Sparkles className="h-5 w-5 text-muted-foreground" />
    </div>
);

const GearIcon = () => (
    <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center">
        <SlidersHorizontal className="h-5 w-5 text-muted-foreground" />
    </div>
);

type ProductConfigType = {
    filters: ProductData
    filterdValue?: FilterType
    setFilterdValue?: Dispatch<SetStateAction<FilterType | null>>
    isCad?: boolean
    handleCatalogCreateOrder?: (customerId?: string) => void
    shareProductTouser?: string
    isValidSelection?: () => string | true | undefined,
    isPending?: boolean

    isAdmin?: boolean
    customerData?: Customer[]
    selectedProduct?: FilterType
    actualProducts?: any[]
    onGenerateShareLink?: (includePrice?: boolean) => Promise<void>
    isGeneratingLink?: boolean
    generatedShareLink?: string
}

const ProductConfig: React.FC<ProductConfigType> = ({
    filters,
    setFilterdValue,
    isCad,
    handleCatalogCreateOrder,
    shareProductTouser: shareProductTouserProp,
    isValidSelection,
    isPending,
    isAdmin,
    customerData,
    selectedProduct,
    actualProducts,
    onGenerateShareLink,
    isGeneratingLink,
    generatedShareLink
}) => {
    const [diameter, setDiameter] = useState<string>('');
    const [caratWeight, setCaratWeight] = useState<string>();
    const [diamondQuality, setDiamondQuality] = useState<string>('');
    const [selectedMetalId, setSelectedMetalId] = useState<string>('');
    const [quantity, setQuantity] = useState<number>(1);
    const [selectedCustomer, setSelectedCustomer] = useState<string | undefined>()

    useEffect(() => {
        console.log('selectedCustomer', selectedCustomer)
    }, [selectedCustomer])
    const [specialInstructions, setSpecialInstructions] = useState<string>("");

    // const selectedMetal = useMemo(() => {
    //     return METAL_OPTIONS.find(m => m.id === selectedMetalId) || METAL_OPTIONS[0];
    // }, [selectedMetalId]);

    // const selectedMetalDisplay = useMemo(() => {
    //     if (selectedMetal.group === 'Platinum') {
    //         return 'Platinum';
    //     }
    //     return `${selectedMetal.karat.toUpperCase()} KT ${selectedMetal.group}`;
    // }, [selectedMetal]);

    const handleQuantityIncrease = () => setQuantity(q => q + 1);
    const handleQuantityDecrease = () => setQuantity(q => Math.max(1, q - 1));
    useEffect(() => {
        if (setFilterdValue) setFilterdValue({
            diameter,
            caratWeight: caratWeight!,
            diamondQuality,
            selectedMetalId,
            quantity,
            specialInstructions,
        });
    }, [diameter, caratWeight, diamondQuality, selectedMetalId, quantity, specialInstructions]);


    const filteredMetals = METAL_OPTIONS.filter(metal =>
        filters?.metal_type?.includes(metal.id) || filters?.metal_type?.includes(metal.id2)
    );

    // Compute which options are available given current selections
    const availableOptions = useMemo(() => {
        if (!actualProducts?.length) return { diameter: new Set<string>(), caratWeight: new Set<string>(), diamondQuality: new Set<string>(), metalType: new Set<string>() };

        const checkAvailable = (field: string, fieldValue: string, skipField: string) => {
            return actualProducts.some((p: any) => {
                const matchDiameter = skipField === 'diameter' || !diameter || p.diameter === diameter;
                const matchCarat = skipField === 'caratWeight' || !caratWeight || p.carat_weight?.toString() === caratWeight;
                const matchQuality = skipField === 'diamondQuality' || !diamondQuality || p.diamond_quality === diamondQuality;
                const matchMetal = skipField === 'metalType' || !selectedMetalId || p.metal_type === selectedMetalId;

                if (field === 'diameter') return p.diameter === fieldValue && matchCarat && matchQuality && matchMetal;
                if (field === 'caratWeight') return p.carat_weight?.toString() === fieldValue && matchDiameter && matchQuality && matchMetal;
                if (field === 'diamondQuality') return p.diamond_quality === fieldValue && matchDiameter && matchCarat && matchMetal;
                if (field === 'metalType') return (p.metal_type === fieldValue) && matchDiameter && matchCarat && matchQuality;
                return false;
            });
        };

        const availDiameters = new Set<string>();
        const availCarats = new Set<string>();
        const availQualities = new Set<string>();
        const availMetals = new Set<string>();

        filters?.diameter?.forEach(d => { if (checkAvailable('diameter', d, 'diameter')) availDiameters.add(d); });
        filters?.carat_weight?.forEach(c => { if (checkAvailable('caratWeight', c.toString(), 'caratWeight')) availCarats.add(c.toString()); });
        filters?.diamond_quality?.forEach(q => { if (checkAvailable('diamondQuality', q, 'diamondQuality')) availQualities.add(q); });
        filters?.metal_type?.forEach(m => { if (checkAvailable('metalType', m, 'metalType')) availMetals.add(m); });

        return { diameter: availDiameters, caratWeight: availCarats, diamondQuality: availQualities, metalType: availMetals };
    }, [actualProducts, filters, diameter, caratWeight, diamondQuality, selectedMetalId]);

    useEffect(() => {
        if (!filters) return;

        const diameters = filters.diameter ?? [];
        const carats = filters.carat_weight ?? [];
        const qualities = filters.diamond_quality ?? [];
        const metals = filters.metal_type ?? [];

        if (!diameter && diameters.length > 0) {
            const first = diameters.find(d => availableOptions.diameter.has(d as string)) ?? diameters[0];
            setDiameter(first as string);
        }
        if (!caratWeight && carats.length > 0) {
            const first = carats.find(c => availableOptions.caratWeight.has(c.toString())) ?? carats[0];
            setCaratWeight(first as any);
        }
        if (!diamondQuality && qualities.length > 0) {
            const first = qualities.find(q => availableOptions.diamondQuality.has(q as string)) ?? qualities[0];
            setDiamondQuality(first as string);
        }
        if (!selectedMetalId && metals.length > 0) {
            const firstMetal = filteredMetals.find(m =>
                availableOptions.metalType.has(m.id) || availableOptions.metalType.has(m.id2)
            ) ?? filteredMetals[0];
            if (firstMetal) setSelectedMetalId(firstMetal.id2);
        }
    }, [filters, actualProducts, diameter, caratWeight, diamondQuality, selectedMetalId, availableOptions, filteredMetals]);

    const selectedMetal = useMemo(() => {
        return METAL_OPTIONS.find(m => m.id2 === selectedMetalId) || METAL_OPTIONS[0];
    }, [selectedMetalId]);

    const selectedMetalDisplay = useMemo(() => {
        if (selectedMetal?.group === 'Platinum') {
            return 'Platinum';
        }
        return `${selectedMetal?.karat} ${selectedMetal?.group}`;
    }, [selectedMetal]);

    const [openModal, setOpenModal] = useState(false)
    const [copied, setCopied] = useState(false)

    const shareLink = generatedShareLink || shareProductTouserProp || ''

    const handleCopy = async () => {
        await navigator.clipboard.writeText(shareLink)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }
    return (
        <div>
            <div className="bg-card rounded-md border border-border">
                <div>
                    <header className="flex items-center space-x-4 p-4 border-b border-border">
                        <GearIcon />
                        <h1 className="text-body1 text-foreground">Product Configuration</h1>
                    </header>

                    <div className="px-4 flex flex-col gap-8 py-6">
                        {
                            filters?.diameter?.length && filters.diameter.length > 0 ? <ConfigRow label="Diameter" value={diameter}>
                                {filters.diameter.map((option) => (
                                    <OptionButton key={option} label={option} isSelected={diameter === option} onClick={() => setDiameter(option)} disabled={!availableOptions.diameter.has(option)} />
                                ))}
                            </ConfigRow> : null
                        }

                        {filters?.metal_type?.length && filters.metal_type.length > 0 ? (
                            <ConfigRow label="Metal Type" value={selectedMetalDisplay}>
                                {filteredMetals.map((metal) => (
                                    <MetalButton
                                        key={metal.id}
                                        label={metal.karat}
                                        colorClass={metal.colorClass}
                                        textColorClass={metal.textColorClass}
                                        isSelected={selectedMetalId === metal.id2}
                                        onClick={() => setSelectedMetalId(metal.id2)}
                                        disabled={!availableOptions.metalType.has(metal.id) && !availableOptions.metalType.has(metal.id2)}
                                    />
                                ))}
                            </ConfigRow>
                        ) : null}

                        {
                            filters?.diamond_quality?.length && filters.diamond_quality.length > 0 ? <ConfigRow label="Diamond Quality" value={formatDiamondQuality(diamondQuality)}>
                                {filters.diamond_quality.map((option) => (
                                    <OptionButton key={option} label={formatDiamondQuality(option)} isSelected={diamondQuality === option} onClick={() => setDiamondQuality(option)} disabled={!availableOptions.diamondQuality.has(option)} />
                                ))}
                            </ConfigRow> : null
                        }

                        {
                            filters?.carat_weight?.length && filters.carat_weight.length > 0 ? <ConfigRow label="Carat Weight" value={formatCaratWeight(caratWeight)}>
                                {filters.carat_weight.map((option) => (
                                    <OptionButton key={option} label={formatCaratWeight(option)} isSelected={caratWeight?.toString() === option} onClick={() => setCaratWeight(option)} disabled={!availableOptions.caratWeight.has(option.toString())} />
                                ))}
                            </ConfigRow> : null
                        }

                        <div className="flex flex-col items-start md:flex-row md:items-center md:justify-between">
                            <Label className="text-body2 text-muted-foreground font-medium mb-2 md:mb-0">Quantity:</Label>
                            <QuantitySelector quantity={quantity} onIncrease={handleQuantityIncrease} onDecrease={handleQuantityDecrease} />
                        </div>
                        {
                            isAdmin && <div>
                                <Label className="text-body2 text-muted-foreground font-medium mb-2">Customer</Label>
                                <Select value={selectedCustomer} onValueChange={setSelectedCustomer}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder="Select Customer" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {customerData?.map((p) => (
                                            <SelectItem key={p.customer_id} value={String(p.customer_id)}>
                                                {p.business_name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        }
                        <div>
                            <h3 className="text-body2 font-medium text-foreground mb-1">Price Quotation</h3>
                            <p className="text-body2 text-muted-foreground">
                                Prices quoted are for stock variants (finger size 6, bracelet length 7 inch, necklace length 18 inch). Please note modifications to finger size or length in Special Instructions below.
                            </p>
                        </div>

                        <div>
                            <h3 className="text-body2 font-medium text-foreground mb-1">Lead Time</h3>
                            <p className="text-body2 text-muted-foreground">
                                You will receive an order confirmation within <span className="font-bold">24 hours</span>. In stock items will ship within 1 business day. Made to order items will ship in ~3 weeks.
                            </p>
                        </div>

                        <div>
                            <Label htmlFor="special-instructions" className="text-body2 text-muted-foreground font-medium mb-2">Special Instructions</Label>
                            <Textarea
                                id="special-instructions"
                                value={specialInstructions}
                                onChange={(e) => setSpecialInstructions(e.target.value)}
                                className="w-full h-24"
                                placeholder="Enter any special requests here..."
                            />
                        </div>

                        <div className="flex flex-col sm:flex-row items-center gap-4 pt-4">
                            <Button type="submit" size="lg" className="w-full sm:w-auto px-8" onClick={() => handleCatalogCreateOrder?.(selectedCustomer)} isLoading={isPending}>
                                Submit Order Request
                            </Button>
                            <Button variant="outline" size="lg" type="button" className="w-full sm:w-auto px-8" onClick={() => {
                                if (!isValidSelection?.()) {
                                    toast.error('Please select product configuration')
                                    return;
                                }
                                setOpenModal(true)
                            }}>
                                Share to Customer
                            </Button>
                        </div>
                        <Dialog open={openModal} onOpenChange={setOpenModal}>
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle className='text-body1 font-medium'>Share this product</DialogTitle>
                                    <DialogDescription className='text-body2'>
                                        {shareLink ? 'Copy the link below and share it with your customer.' : 'Generate a shareable link for this product configuration.'}
                                    </DialogDescription>
                                </DialogHeader>

                                {!shareLink ? (
                                    <div className="mt-4 flex flex-col gap-2">
                                        <Button onClick={() => onGenerateShareLink?.(true)} isLoading={isGeneratingLink} className="w-full">
                                            Generate Link
                                        </Button>
                                        <Button variant="outline" onClick={() => onGenerateShareLink?.(false)} disabled={isGeneratingLink} className="w-full">
                                            Generate Link Without Price
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2 mt-4">
                                        <Input value={shareLink} readOnly className="flex-1 h-11 text-body2 bg-muted focus-visible:ring-0" />
                                        <Button onClick={handleCopy} className="h-11 px-6 min-w-[100px]">
                                            {copied ? "Copied!" : "Copy"}
                                        </Button>
                                    </div>
                                )}
                            </DialogContent>
                        </Dialog>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductConfig;
